import { useEffect, useState } from "react";
import { ICON_BASE_PATH } from "../../../../utils/config";
import { TextBox, type TextBoxChangeEvent } from "@progress/kendo-react-inputs";
import { t } from "i18next";

interface NickNameProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onValidate?: (isValid: boolean) => void;
  onClearError?: () => void; 
  showStarLabel?: boolean;
  max?: number;
  minLength?: number;
  disable?: boolean;
  triggerValidation?: boolean | string;
  nickNameError?: string;
}

const NickName: React.FC<NickNameProps> = ({
  label,
  value,
  onChange,
  onValidate,
  onClearError,
  showStarLabel = true,
  max = 30,
  minLength = 2,
  disable = false,
  triggerValidation,
  nickNameError,
}) => {
  const [touched, setTouched] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  // Sanitize user input
  const sanitize = (s: string) => {
    const cleaned = s
      .replace(/[^\p{L}\p{N}\s]/gu, "") 
      .replace(/\s+/g, " ")
      .trim();

    return cleaned.replace(/\b(\p{L})(\p{L}*)/gu, (_, first, rest) => {
      return first.toUpperCase() + (rest ? rest.toLowerCase() : "");
    });
  };

  // Validate function — returns the error message (empty string = valid)
  const validate = (val: string): string => {
    let msg = "";

    if (!val || val.trim() === "") {
      msg = `${t("please_enter_the")} ${label}.`;
    } else if (val.trim().length < minLength) {
      msg = `${label} ${t("must_be_at_least")} ${minLength} ${t("characters")}.`;
    } else if (val.length > max) {
      msg = `${label} ${t("cannot_exceed")} ${max} ${t("characters")}.`;
    }

    setValidationMessage(msg);

    const isValid = msg === "";
    if (onValidate) onValidate(isValid);

    // If now valid, tell the parent to clear its nickNameError
    if (isValid && onClearError) onClearError();

    return msg;
  };

  const handleChange = (e: TextBoxChangeEvent) => {
    const raw = String(e.value ?? "");
    const filtered = sanitize(raw).slice(0, max);
    onChange(filtered);

    if (touched) validate(filtered);
  };

  const handleBlur = () => {
    setTouched(true);
    validate(value); // re-validate on blur — clears error if now valid
  };

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      validate(value);
    }
  }, [triggerValidation]);

  // Determine the message to display:
  // - Local validationMessage takes priority (reflects latest input state)
  // - Fall back to parent nickNameError only when there's no local message
  // - Never show parent nickNameError if local validation says the field is valid
  const displayError =
    touched && validationMessage
      ? validationMessage
      : touched && !validationMessage && nickNameError
        ? nickNameError
        : "";

  const isInvalid = touched && displayError !== "";

  return (
    <div className="gx_input_container">
      <label
        className={`input-label gx-font-semibold ${value ? "floating" : ""}`}
      >
        {label}
        {showStarLabel && (
          <span
            className={`gx_input_star ${
              value ? "gx_input_star_float" : "gx_input_star_normal"
            }`}
          >
            *
          </span>
        )}
      </label>

      <div className={`gx_input_cont ${isInvalid ? "invalid" : "valid"}`}>
        <img src={`${ICON_BASE_PATH}name-input.svg`} alt="Name" />

        <TextBox
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          type="text"
          autoComplete="off"
          disabled={disable}
          className="gx-textbox-css"
          maxLength={max}
          aria-invalid={isInvalid}
        />
      </div>

      {displayError && (
        <div className="gx_input_validationmessage gx-font-regular">
          {displayError}
        </div>
      )}
    </div>
  );
};

export default NickName;
